import React, { useState } from 'react';
import { useFocusTrap } from '../../hooks/useFocusTrap';

const sections = [
  {
    id: 'getting-started',
    title: 'Getting Started',
    content: [
      {
        heading: 'Welcome to Blessio',
        body: `Blessio is a fund-accounting bookkeeping tool built for non-profit churches. It tracks income and expenses across multiple funds, reconciles bank accounts, and produces financial reports your board and congregation can rely on.`,
      },
      {
        heading: "How it's organized",
        body: `Everything flows through three concepts:\n\n• **Accounts** — your actual bank accounts (e.g. Checking, Savings).\n• **Funds** — the purpose money is set aside for (e.g. Church, Food Bank, Sunday School). Each fund is linked to one bank account.\n• **Transaction Types** — categories like Utilities, Pastor, Offering. Each type is tagged as a Bill (money out) or Receipt (money in).`,
      },
      {
        heading: 'First-time setup',
        body: `Go to **Admin** in the sidebar and work through the tabs in order:\n\n1. **Accounts** — add your bank accounts.\n2. **Funds** — add your funds and link each to an account.\n3. **Transaction Types** — add the categories you use.\n4. **Budget** — optionally set monthly budget targets.\n5. **Users** — invite your bookkeeper or treasurer.\n6. **Organization** — set your church name and logo.`,
      },
    ],
  },
  {
    id: 'dashboard',
    title: 'Dashboard',
    content: [
      {
        heading: 'Current Balances',
        body: `The balance card shows two columns for each bank account:\n\n• **Current** — your book balance. Includes every transaction you've entered, whether or not it has cleared the bank yet. This is your running total.\n• **Bank** — your cleared balance. Only counts transactions you've marked as cleared (✓). This should match your bank statement.\n\nThe difference between Current and Bank is shown as **outstanding** — money you've recorded but the bank hasn't processed yet (e.g. a cheque in the mail).\n\nBelow each account you'll see the balance broken down by fund.`,
      },
      {
        heading: 'Monthly Cash Flow chart',
        body: `The chart shows income and expenses for each month across up to three years of data.\n\n• **Above the $0 line** — income (money in).\n• **Below the $0 line** — expenses (money out).\n• **Each color** represents a different year. Older years are muted; the current year is the most vivid.\n• **Y-axis** shows the scale: max income at the top, max expense at the bottom.\n• The current month label is bold.\n\nHover over any bar to see the exact amount.`,
      },
    ],
  },
  {
    id: 'transactions',
    title: 'Transactions',
    content: [
      {
        heading: 'Adding a transaction',
        body: `Click **+ Add Transaction** and choose one of three types:\n\n• **Bill** — money going out. Enter the payee, check number (optional), category, fund, and amount.\n• **Receipt** — money coming in. Enter the category, fund, and amount.\n• **Starting Balance** — sets the opening balance for a fund at the start of a year. Not counted as income or expense.`,
      },
      {
        heading: 'Split transactions',
        body: `If one cheque or deposit covers multiple categories or funds, click **+ Add line** to split it. Each line gets its own category and fund. The total across all lines is the transaction amount.\n\nSplit transactions show an **S** badge in the table.`,
      },
      {
        heading: 'Payable To & Check number',
        body: `For bills, you can record who was paid (**Payable To**) and the cheque number. Blessio remembers every payee you've used — start typing and matching names appear in a dropdown, saving you retyping common vendors like your utility company.`,
      },
      {
        heading: 'Cleared (✓)',
        body: `Tick the **Cleared** checkbox directly in the transaction table when the item appears on your bank statement. This moves the amount from your Current balance into your Bank balance, letting you reconcile without opening each transaction to edit it.`,
      },
      {
        heading: 'Filtering transactions',
        body: `Use the **date range** pickers at the top to show any period — across month or year boundaries. You can also filter by **Fund** or **Account** independently. Click **Clear filters** to reset to all funds and accounts.`,
      },
      {
        heading: 'Exporting',
        body: `Click **Export YAML** to download the filtered transactions as a YAML file. The file includes the date range and any active filters in the filename so you always know what it contains.`,
      },
    ],
  },
  {
    id: 'reconcile',
    title: 'Reconcile',
    content: [
      {
        heading: 'What reconciliation means',
        body: `Reconciliation is the process of proving that your books match your bank statement. You work through each transaction for a month and mark it cleared once you see it on the statement. When your cleared balance equals the closing balance on the statement, you're done.`,
      },
      {
        heading: 'How to reconcile',
        body: `1. Open your bank statement for the month.\n2. Go to **Reconcile** in the sidebar and select the account and month.\n3. Tick each transaction that appears on the statement.\n4. The running cleared balance updates as you tick.\n5. When the cleared balance matches the statement closing balance, the month is reconciled.\n\nTransactions that haven't cleared yet (outstanding cheques, pending deposits) will remain unticked and carry forward to next month.`,
      },
    ],
  },
  {
    id: 'reports',
    title: 'Reports',
    content: [
      {
        heading: 'Cash Flow',
        body: `Shows income and expenses grouped by fund for any year, up to any month (Year-to-Date).\n\n• **Summary table** — one row per fund, showing total income, expense, and net.\n• **Detail tables** — one table per fund, breaking down each transaction type with actual vs. budget comparison.\n• **Variance** — positive (green) means ahead of budget for income or under budget for expenses. Negative (red) is the reverse.\n\nUse the year and month selectors at the top to change the period. Click **Print** for a clean printable version.`,
      },
      {
        heading: 'Account Balances',
        body: `Shows current balances organized by bank account with linked funds nested underneath.\n\n• **Account Total (bank)** — the account's opening balance plus all activity for the year.\n• **Fund rows** — how that account balance is allocated across funds.\n• **Reconciliation bar** — green if fund totals match the account total, red if they don't.\n\nFunds not yet linked to an account appear in an amber **Unlinked Funds** section — a reminder to set up the link in Admin → Funds.\n\nThe **Monthly Reconciliation** table at the bottom shows deposits, withdrawals, and running balance month by month for each account.`,
      },
      {
        heading: 'Budget by Month',
        body: `A month-by-month view of every budget line showing actual vs. budgeted amounts.\n\n• Rows are grouped by fund and then by transaction type.\n• Each month column shows the actual amount spent or received.\n• The rightmost column shows the full-year total.\n• Amounts over budget for expenses (or under budget for income) appear in red.`,
      },
    ],
  },
  {
    id: 'admin',
    title: 'Admin',
    content: [
      {
        heading: 'Accounts',
        body: `Bank accounts used for all transactions. Add accounts to match your actual bank accounts (e.g. Checking, Savings). You can disable an account to hide it from new transactions without losing its history. Rename by hovering the name and clicking Edit.`,
      },
      {
        heading: 'Funds',
        body: `Funds are the buckets that money is classified into for reporting. Each fund should be linked to the bank account it lives in — this powers the Account Balances reconciliation and the home page balance breakdown.\n\nExample: General Fund, Food Bank, and Sunday School might all be linked to Checking.`,
      },
      {
        heading: 'Transaction Types',
        body: `Categories for individual transactions (e.g. Utilities, Pastor, Offering). Each type is tagged as a **Bill** (expense) or **Receipt** (income).\n\nWhen entering a transaction you can override which fund a type goes to, so one category can appear under different funds if needed.\n\nYou cannot delete a type that has been used in transactions — disable it instead to prevent it from appearing in new entries.`,
      },
      {
        heading: 'Budget',
        body: `Set a monthly dollar target for any fund + category combination. Budget lines appear in the Cash Flow report and the Budget by Month report as the comparison baseline.\n\nEnter the **monthly** amount — annual totals are calculated automatically. You can set budgets per year; switch years with the selector at the top. Click **Save Budget** when done.`,
      },
      {
        heading: 'Users',
        body: `Manage who can access your organization. Users sign in with their Google account at the email address you enter here.\n\n• **Admin** — can access all features including the Admin panel.\n• **User** — can enter transactions and view reports but cannot change settings.\n\nYou cannot change your own role or remove yourself.`,
      },
      {
        heading: 'Organization',
        body: `Set the name that appears in the sidebar header and upload a logo. The logo replaces the default Blessio icon in the sidebar. Recommended: a square PNG or SVG at least 64×64 pixels.`,
      },
    ],
  },
];

const HelpDrawer = ({ onClose }) => {
  const [activeSection, setActiveSection] = useState('getting-started');
  const section = sections.find(s => s.id === activeSection);
  const drawerRef = useFocusTrap(onClose);

  const renderBody = (text) =>
    text.split('\n').map((line, i) => {
      if (!line.trim()) return <br key={i} />;
      // Bold **text**
      const parts = line.split(/(\*\*[^*]+\*\*)/g);
      return (
        <p key={i} className="mb-1">
          {parts.map((part, j) =>
            part.startsWith('**') && part.endsWith('**')
              ? <strong key={j}>{part.slice(2, -2)}</strong>
              : part
          )}
        </p>
      );
    });

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />

      {/* Drawer */}
      <div
        ref={drawerRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="help-drawer-title"
        tabIndex={-1}
        className="relative z-10 w-full max-w-2xl bg-white shadow-2xl flex flex-col h-full"
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-slate-800 text-white flex-shrink-0">
          <div>
            <h2 id="help-drawer-title" className="text-lg font-bold">Help Guide</h2>
            <p className="text-slate-400 text-xs mt-0.5">Everything you need to know about Blessio</p>
          </div>
          <button onClick={onClose} aria-label="Close" title="Close" data-autofocus-skip className="text-slate-400 hover:text-white text-2xl leading-none">×</button>
        </div>

        <div className="flex flex-1 overflow-hidden">
          {/* Sidebar nav */}
          <nav className="w-44 flex-shrink-0 border-r border-gray-100 bg-slate-50 py-4 overflow-y-auto">
            {sections.map(s => (
              <button
                key={s.id}
                onClick={() => setActiveSection(s.id)}
                className={`w-full text-left px-4 py-2.5 text-sm font-medium transition-colors ${
                  activeSection === s.id
                    ? 'bg-white text-blue-600 border-r-2 border-blue-600'
                    : 'text-slate-600 hover:bg-white hover:text-slate-800'
                }`}
              >
                {s.title}
              </button>
            ))}
          </nav>

          {/* Content */}
          <div className="flex-1 overflow-y-auto px-8 py-6">
            <h2 className="text-xl font-bold text-slate-800 mb-5">{section.title}</h2>
            <div className="space-y-6">
              {section.content.map((item, i) => (
                <div key={i}>
                  <h3 className="text-sm font-semibold text-slate-700 mb-2 pb-1 border-b border-gray-100">
                    {item.heading}
                  </h3>
                  <div className="text-sm text-slate-600 leading-relaxed space-y-1">
                    {renderBody(item.body)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-gray-100 bg-slate-50 flex-shrink-0">
          <p className="text-xs text-slate-500 text-center">
            Blessio · Built for non-profit churches · Contact your administrator for access issues
          </p>
        </div>
      </div>
    </div>
  );
};

export default HelpDrawer;
